import { useFlags } from '@atlaskit/flag';
import { AddEditVehicleFormFields } from 'core/config/form-fields';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import { VehiclesApiService } from 'core/api';
import { useAuthState } from 'core/providers/AuthProvider';
import { IAddEditVehicleFormOutput } from '../types';
import { IVehicleDao, IVehicleDto } from 'core/api/types/vehicles.interface';
import { ISharedFormField } from 'shared/components/form/form-interface';
import { getFuelTypeNameFromKey } from 'core/constants/fuel-types';

const AddEditVehicleDialog = ({ vehicle }: { vehicle?: IVehicleDao }) => {
  const { userData } = useAuthState();
  const flags = useFlags();
  const dialog = useDialog();

  const [formSubmitting, setFormSubmitting] = useState(false);

  const addEditVehicle = async (data: IAddEditVehicleFormOutput) => {
    setFormSubmitting(true);

    const payload: IVehicleDto = {
      active: data.active,
      registration: data.registration,
      make: data.make,
      model: data.model,
      fuelType: data.fuelType.value,
      currentDriver: null,
    };

    try {
      if (vehicle) {
        await VehiclesApiService.update(vehicle.uid, payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      } else {
        await VehiclesApiService.set(payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      }
      showSuccessFlag(
        vehicle ? 'Vehicle updated' : 'Vehicle created',
        `The vehicle has been ${vehicle ? 'updated' : 'created'} successfully`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag(
        vehicle ? 'Vehicle update failed' : 'Vehicle creation failed',
        `Unable to ${vehicle ? 'update' : 'create'} a vehicle, please try again.`,
        flags
      );
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const getDefaultValue = (vehicle: IVehicleDao, key: string) => {
    switch (key) {
      case 'fuelType':
        return {
          value: vehicle.fuelType,
          label: getFuelTypeNameFromKey(vehicle.fuelType),
        };
      default:
        return vehicle[key as keyof IVehicleDao];
    }
  };

  const fields = AddEditVehicleFormFields.map((field) => ({
    ...field,
    defaultValue: vehicle ? getDefaultValue(vehicle, field.key) : field.defaultValue,
  }));

  const customContent = () => {
    return (
      <SharedForm
        className='p-4 overflow-y-auto'
        onSubmit={addEditVehicle}
        fields={fields as ISharedFormField[]}
        buttonLabel='Submit'
        loading={formSubmitting}
        cancelButton={cancelButton}
      />
    );
  };

  return (
    <SharedDialogBase
      title={vehicle ? 'Update vehicle' : 'Add vehicle'}
      customContentTemplate={customContent()}
      showButtons={false}
    />
  );
};

export default AddEditVehicleDialog;
